/**
 * 屏幕 5：系统对战
 * 3 阶段合一屏：选择 → 战斗 → 结算
 * 数据源：localStorage lastGeneration（最近生成的 4 怪）
 */

import { createSnow } from "../snow";
import { generateMonster, type MonsterFrames } from "../spriteLib";
import { getZoo, type ZooEntry } from "../zoo";
import type { MonsterDesc } from "../../shared/types";
import type { Screen2Combo } from "./screen2";

export interface Screen5Deps {
  root: HTMLElement;
  onBack: () => void;
}

export interface Screen5Controller {
  destroy: () => void;
}

type Phase = "select" | "battle" | "result";

const COMBAT_TICK_MS = 480;

function powerOf(m: MonsterDesc): number {
  const t = m.threat === "Keter" ? 30 : m.threat === "Euclid" ? 20 : 10;
  return t + m.features.length * 3;
}

function hpMaxOf(m: MonsterDesc): number {
  return 80 + (m.threat === "Keter" ? 30 : m.threat === "Euclid" ? 20 : 10);
}

interface BattleRound {
  attacker: "A" | "B";
  damage: number;
  text: string;
  crit: boolean;
}

function simulateBattle(a: ZooEntry, b: ZooEntry): {
  winner: ZooEntry;
  loser: ZooEntry;
  log: BattleRound[];
  finalHpA: number;
  finalHpB: number;
} {
  let hpA = hpMaxOf(a.monster);
  let hpB = hpMaxOf(b.monster);
  const log: BattleRound[] = [];
  const maxRounds = 20;

  for (let round = 1; round <= maxRounds; round++) {
    if (hpA <= 0 || hpB <= 0) break;
    // A 攻击 B
    const baseA = Math.floor(8 + Math.random() * 12 + powerOf(a.monster) / 4 - powerOf(b.monster) / 6);
    const critA = Math.random() < 0.18;
    const dmgA = Math.max(1, critA ? baseA * 2 : baseA);
    hpB -= dmgA;
    log.push({
      attacker: "A",
      damage: dmgA,
      text: critA
        ? `【暴击】${a.monster.scpId} 撕裂 ${b.monster.scpId}，造成 ${dmgA} 伤害`
        : `${a.monster.scpId} 发起攻击 → ${b.monster.scpId} 受到 ${dmgA} 伤害`,
      crit: critA,
    });
    if (hpB <= 0) break;
    // B 攻击 A
    const baseB = Math.floor(8 + Math.random() * 12 + powerOf(b.monster) / 4 - powerOf(a.monster) / 6);
    const critB = Math.random() < 0.18;
    const dmgB = Math.max(1, critB ? baseB * 2 : baseB);
    hpA -= dmgB;
    log.push({
      attacker: "B",
      damage: dmgB,
      text: critB
        ? `【暴击】${b.monster.scpId} 撕裂 ${a.monster.scpId}，造成 ${dmgB} 伤害`
        : `${b.monster.scpId} 发起攻击 → ${a.monster.scpId} 受到 ${dmgB} 伤害`,
      crit: critB,
    });
  }
  const winner = hpA > hpB ? a : b;
  const loser = hpA > hpB ? b : a;
  return { winner, loser, log, finalHpA: Math.max(0, hpA), finalHpB: Math.max(0, hpB) };
}

export function createScreen5(deps: Screen5Deps): Screen5Controller {
  const { root, onBack } = deps;
  const destroySnow = createSnow(root);

  const screen = document.createElement("div");
  screen.className = "screen-5";

  const title = document.createElement("div");
  title.className = "title";
  title.textContent = "系统对战";
  screen.appendChild(title);

  const body = document.createElement("div");
  body.className = "battle-body";
  screen.appendChild(body);

  root.appendChild(screen);

  let phase: Phase = "select";
  let selected: number[] = [];
  let battleResult: ReturnType<typeof simulateBattle> | null = null;
  let activeTimers: number[] = [];

  function clearTimers() {
    activeTimers.forEach((id) => window.clearTimeout(id));
    activeTimers = [];
  }

  function render() {
    body.innerHTML = "";
    if (phase === "select") renderSelect();
    else if (phase === "battle") renderBattle();
    else if (phase === "result" && battleResult) renderResult(battleResult);
  }

  function renderSelect() {
    const entries = getZoo();

    if (entries.length < 2) {
      const empty = document.createElement("div");
      empty.className = "battle-empty";
      empty.innerHTML = "捕获图鉴里需要至少 2 只怪<br/><small>去主页输入名字 → 选中怪物（进入详情页即入园）再来</small>";
      body.appendChild(empty);

      const back = document.createElement("button");
      back.className = "primary";
      back.textContent = "返回主屏";
      back.addEventListener("click", onBack);
      const actions = document.createElement("div");
      actions.className = "actions";
      actions.appendChild(back);
      body.appendChild(actions);
      return;
    }

    const hint = document.createElement("div");
    hint.className = "battle-hint";
    hint.textContent = `选择 2 只开战（已选 ${selected.length}/2）`;
    body.appendChild(hint);

    const grid = document.createElement("div");
    grid.className = "battle-grid";
    body.appendChild(grid);

    entries.forEach((entry, i) => {
      const card = createBattleCard(entry);
      card.classList.toggle("selected", selected.includes(i));
      card.addEventListener("click", () => {
        if (selected.includes(i)) {
          selected = selected.filter((x) => x !== i);
        } else {
          if (selected.length >= 2) return;
          selected = [...selected, i];
        }
        render();
      });
      grid.appendChild(card);
    });

    const actions = document.createElement("div");
    actions.className = "actions";
    const start = document.createElement("button");
    start.className = "primary";
    start.textContent = selected.length === 2 ? "开始对战" : `再选 ${2 - selected.length} 只`;
    start.disabled = selected.length !== 2;
    start.addEventListener("click", () => {
      if (selected.length !== 2) return;
      battleResult = simulateBattle(entries[selected[0]], entries[selected[1]]);
      phase = "battle";
      render();
    });
    const back = document.createElement("button");
    back.className = "secondary";
    back.textContent = "返回主屏";
    back.addEventListener("click", onBack);
    actions.appendChild(start);
    actions.appendChild(back);
    body.appendChild(actions);
  }

  function createBattleCard(entry: ZooEntry): HTMLElement {
    const card = document.createElement("div");
    card.className = "battle-card";
    // 顶部：观众输入的名字（高亮标签）
    if (entry.originalName) {
      const nameTag = document.createElement("div");
      nameTag.className = "card-name-tag";
      nameTag.textContent = `「${entry.originalName}」`;
      card.appendChild(nameTag);
    }
    // 中间：canvas + meta 一行
    const row = document.createElement("div");
    row.className = "card-row";
    const canvas = document.createElement("canvas");
    canvas.width = 128;
    canvas.height = 128;
    const frames = generateMonster(
      entry.monster,
      undefined,
      entry.combo.bodyType,
      entry.combo.paletteName,
      entry.combo.variant
    );
    drawMonsterIdle(canvas, frames);
    row.appendChild(canvas);

    const meta = document.createElement("div");
    meta.className = "card-meta";
    meta.innerHTML = `
      <div class="card-scpid">${entry.monster.scpId}</div>
      <div class="card-class">${entry.monster.class}</div>
      <div class="card-stats">
        <span class="stat-threat threat-${entry.monster.threat}">${entry.monster.threat}</span>
        <span class="stat-power">⚡ ${powerOf(entry.monster)}</span>
        <span class="stat-hp">♥ ${hpMaxOf(entry.monster)}</span>
      </div>
    `;
    row.appendChild(meta);
    card.appendChild(row);
    return card;
  }

  function drawMonsterIdle(canvas: HTMLCanvasElement, frames: MonsterFrames) {
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    const tmp = document.createElement("canvas");
    tmp.width = 64;
    tmp.height = 64;
    tmp.getContext("2d")!.putImageData(frames.frame1, 0, 0);
    ctx.drawImage(tmp, 0, 0, 128, 128);
  }

  function renderBattle() {
    if (!battleResult) {
      phase = "select";
      render();
      return;
    }
    const { winner, loser, log, finalHpA, finalHpB } = battleResult;
    // 注意：battleResult 的 winner/loser 是按 hpA > hpB 决定的，但 A/B 是按 selected[0]/selected[1]
    // 我们需要重新确定 A/B：先看 selected[0] 的 hp 是 finalHpA 还是 finalHpB
    const entries = getZoo();
    const a = entries[selected[0]];
    const b = entries[selected[1]];

    const stage = document.createElement("div");
    stage.className = "battle-stage";

    const cardA = createFighterCard(a, "A");
    const cardB = createFighterCard(b, "B");
    stage.appendChild(cardA);
    stage.appendChild(cardB);

    const logBox = document.createElement("div");
    logBox.className = "battle-log";
    logBox.innerHTML = `<div class="log-prelude">战斗开始 · ${a.monster.scpId} ⚔ ${b.monster.scpId}</div>`;
    stage.appendChild(logBox);

    body.appendChild(stage);

    // 顺序播放战报
    let i = 0;
    const aFighterEl = cardA.querySelector(".fighter-card") as HTMLElement;
    const bFighterEl = cardB.querySelector(".fighter-card") as HTMLElement;
    const hpBarA = cardA.querySelector(".hp-fill") as HTMLElement;
    const hpBarB = cardB.querySelector(".hp-fill") as HTMLElement;
    const hpNumA = cardA.querySelector(".hp-num") as HTMLElement;
    const hpNumB = cardB.querySelector(".hp-num") as HTMLElement;
    const hpMaxA = hpMaxOf(a.monster);
    const hpMaxB = hpMaxOf(b.monster);
    let curHpA = hpMaxA;
    let curHpB = hpMaxB;
    hpBarA.style.width = "100%";
    hpBarB.style.width = "100%";
    hpNumA.textContent = `${curHpA}/${hpMaxA}`;
    hpNumB.textContent = `${curHpB}/${hpMaxB}`;

    function showNext() {
      if (i >= log.length) {
        // 跳到结算
        const t = window.setTimeout(() => {
          phase = "result";
          render();
        }, 800);
        activeTimers.push(t);
        return;
      }
      const r = log[i++];
      const line = document.createElement("div");
      line.className = r.crit ? "log-line log-crit" : "log-line";
      line.textContent = r.text;
      logBox.appendChild(line);
      logBox.scrollTop = logBox.scrollHeight;

      // 震动 + 扣血
      if (r.attacker === "A") {
        curHpB = Math.max(0, curHpB - r.damage);
        hpBarB.style.width = `${(curHpB / hpMaxB) * 100}%`;
        hpNumB.textContent = `${curHpB}/${hpMaxB}`;
        shake(bFighterEl, r.crit);
      } else {
        curHpA = Math.max(0, curHpA - r.damage);
        hpBarA.style.width = `${(curHpA / hpMaxA) * 100}%`;
        hpNumA.textContent = `${curHpA}/${hpMaxA}`;
        shake(aFighterEl, r.crit);
      }
      const t = window.setTimeout(showNext, COMBAT_TICK_MS);
      activeTimers.push(t);
    }
    const startT = window.setTimeout(showNext, 600);
    activeTimers.push(startT);
  }

  function shake(el: HTMLElement, crit: boolean) {
    el.classList.remove("shake", "shake-crit");
    void el.offsetWidth;
    el.classList.add(crit ? "shake-crit" : "shake");
    const t = window.setTimeout(() => {
      el.classList.remove("shake", "shake-crit");
    }, 400);
    activeTimers.push(t);
  }

  function createFighterCard(entry: ZooEntry, label: "A" | "B"): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "fighter";

    const tag = document.createElement("div");
    tag.className = "fighter-tag";
    tag.textContent = `【${label}】`;
    wrap.appendChild(tag);

    const card = document.createElement("div");
    card.className = "fighter-card";

    const canvas = document.createElement("canvas");
    canvas.width = 192;
    canvas.height = 192;
    const frames = generateMonster(
      entry.monster,
      undefined,
      entry.combo.bodyType,
      entry.combo.paletteName,
      entry.combo.variant
    );
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    const tmp = document.createElement("canvas");
    tmp.width = 64;
    tmp.height = 64;
    tmp.getContext("2d")!.putImageData(frames.frame1, 0, 0);
    ctx.drawImage(tmp, 0, 0, 192, 192);
    card.appendChild(canvas);

    const meta = document.createElement("div");
    meta.className = "fighter-meta";
    meta.innerHTML = `
      <div class="fighter-scpid">${entry.monster.scpId}</div>
      <div class="fighter-class">${entry.monster.class}</div>
      <div class="hp-bar"><div class="hp-fill"></div></div>
      <div class="hp-num">--/--</div>
    `;
    card.appendChild(meta);

    wrap.appendChild(card);
    return wrap;
  }

  function renderResult(result: NonNullable<typeof battleResult>) {
    const { winner, loser, log, finalHpA, finalHpB } = result;
    const entries = getZoo();
    const a = entries[selected[0]];
    const b = entries[selected[1]];

    const stage = document.createElement("div");
    stage.className = "battle-result";

    const cardA = createFighterCard(a, "A");
    const cardB = createFighterCard(b, "B");
    const aFighterEl = cardA.querySelector(".fighter-card") as HTMLElement;
    const bFighterEl = cardB.querySelector(".fighter-card") as HTMLElement;
    aFighterEl.classList.add(winner === a ? "winner" : "loser");
    bFighterEl.classList.add(winner === b ? "winner" : "loser");
    const aHp = cardA.querySelector(".hp-fill") as HTMLElement;
    const bHp = cardB.querySelector(".hp-fill") as HTMLElement;
    const aNum = cardA.querySelector(".hp-num") as HTMLElement;
    const bNum = cardB.querySelector(".hp-num") as HTMLElement;
    aHp.style.width = `${(finalHpA / hpMaxOf(a.monster)) * 100}%`;
    bHp.style.width = `${(finalHpB / hpMaxOf(b.monster)) * 100}%`;
    aNum.textContent = `${finalHpA}/${hpMaxOf(a.monster)}`;
    bNum.textContent = `${finalHpB}/${hpMaxOf(b.monster)}`;
    stage.appendChild(cardA);
    stage.appendChild(cardB);

    const verdict = document.createElement("div");
    verdict.className = "verdict";
    const winnerName = winner.monster.scpId;
    const totalDmgA = log.filter((r) => r.attacker === "A").reduce((s, r) => s + r.damage, 0);
    const totalDmgB = log.filter((r) => r.attacker === "B").reduce((s, r) => s + r.damage, 0);
    verdict.innerHTML = `
      <div class="verdict-text">🏆 ${winnerName} 胜利</div>
      <div class="verdict-detail">${log.length} 回合 · ${a.monster.scpId} 造成 ${totalDmgA} / ${b.monster.scpId} 造成 ${totalDmgB}</div>
    `;
    stage.appendChild(verdict);

    body.appendChild(stage);

    const actions = document.createElement("div");
    actions.className = "actions";
    const again = document.createElement("button");
    again.className = "primary";
    again.textContent = "再战（同 4 怪）";
    again.addEventListener("click", () => {
      selected = [];
      phase = "select";
      render();
    });
    const back = document.createElement("button");
    back.className = "secondary";
    back.textContent = "返回主屏";
    back.addEventListener("click", onBack);
    actions.appendChild(again);
    actions.appendChild(back);
    body.appendChild(actions);
  }

  render();

  return {
    destroy() {
      clearTimers();
      destroySnow();
      screen.remove();
    },
  };
}
